'use client'

import { Auth0Provider } from '@auth0/auth0-react'
import createCache from '@emotion/cache'
import { CacheProvider } from '@emotion/react'
import { LicenseInfo } from '@mui/x-license'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import 'prismjs'
import 'prismjs/components/prism-jsx'
import 'prismjs/components/prism-tsx'
import type { PropsWithChildren, ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Provider as ReduxProvider } from 'react-redux'
import { useLocation } from 'react-use'
import { PersistGate } from 'redux-persist/integration/react'

import { SettingsProvider } from '@core/context/settingsContext'
import ThemeComponent from '@core/theme/ThemeComponent'

import AuthWrapper from '~/auth/AuthWrapper'
import getCustomerAssets from '~/auth/assets'
import TenantDiscoveryWrapper from '~/auth/components/TenantDiscovery'
import useHasAuthSession from '~/auth/hooks/useHasAuthSession'
import { LoadingMessage } from '~/components/Loaders'
import Snackbar from '~/components/Snackbar'
import { BackendBootstrap, BackendSwitcher } from '~/components/dev'
import { FeatureFlagsLauncher } from '~/features/featureFlags'
// Add yup validation extensions
import '~/components/forms/yup'
import { defaultACLObj } from '~/configs/acl'
import envConfig from '~/configs/env'
import logAppVersion from '~/configs/version'
import { DataManagementProvider, useIndexedDbCleanup } from '~/data/api/manager'
import { useBootMark } from '~/debug/bootProfiler'
import { FullscreenModeProvider } from '~/hooks/useFullscreenMode'
import { AuthenticatedStatsigClientProvider, UnauthenticatedStatsigClientProvider } from '~/layouts/StatsigProvider'
import UserLayout from '~/layouts/UserLayout'
import Rollbar from '~/layouts/errors/Rollbar'
import { persistor, store } from '~/store'
import { useCustomerAssetsByTenant, useTenantInfo } from '~/store/slices/tenant'

import LocalizationProvider from './LocalizationProvider'
import AclGuard from './components/acl/AclGuard'
import AuthGuard from './components/acl/AuthGuard'

const emotionCache = createCache({ key: 'css' })

logAppVersion()

if (envConfig.muiXLicenseKey) {
  LicenseInfo.setLicenseKey(envConfig.muiXLicenseKey)
} else if (typeof window !== 'undefined') {
  // Dynamic import: static import of the logger triggers Datadog browser-log
  // init at module scope, which fails during Next.js static page generation.
  import('~/debug/logger').then(({ logger }) => {
    logger.warn('MUI X license key is not set. Set NEXT_PUBLIC_MUI_X_LICENSE_KEY in your environment.')
  })
}

export default function AppContainer({ children }: { children: ReactNode }) {
  const pathname = usePathname()
  useBootMark({ phase: 'app-container-mount' })
  useEffect(() => {
    const { favicon } = getCustomerAssets() ?? {}
    if (favicon) {
      const iconLink = document.querySelector("link[rel~='icon']")
      iconLink?.setAttribute('href', favicon)
    }
  }, [pathname])

  return (
    <Rollbar>
      <ReduxProvider store={store}>
        <PersistGate persistor={persistor}>
          <PostPersistMark />
          <CacheProvider value={emotionCache}>
            <SettingsProvider>
              <BackendBootstrap />
              <AppPages pathname={pathname}>{children}</AppPages>
              <BackendSwitcher />
            </SettingsProvider>
          </CacheProvider>
        </PersistGate>
      </ReduxProvider>
    </Rollbar>
  )
}

// Fires once PersistGate has finished rehydrating Redux from localStorage.
// Rendered as a sibling so it gates on the same lift signal as the children.
function PostPersistMark() {
  useBootMark({ phase: 'persist-hydrated' })
  return null
}

type AppPagesProps = {
  pathname: string
} & PropsWithChildren

function AppPages({ pathname, children }: AppPagesProps) {
  const tenantInfo = useTenantInfo()
  const hasAuthSession = useHasAuthSession()
  const [tenantJustSet, setTenantJustSet] = useState(false)
  const [auth0ConfirmedUnauth, setAuth0ConfirmedUnauth] = useState(false)
  const searchParams = useSearchParams()
  const hasCodeParam = !!searchParams?.get('code')

  // Only bypass TenantDiscovery if:
  // - OR (tenantInfo && (hasAuthSession || tenantJustSet))
  // (only tenantInfo is not enough because it might be the wrong info)
  // - OR (code param is present in url)
  // - OR (we are hitting the bypass endpoint from an IdP-Initiated SSO flow)
  const shouldBypassTenantDiscovery =
    (tenantInfo && (hasAuthSession || tenantJustSet)) ||
    hasCodeParam ||
    pathname.includes('auth/oauth') ||
    pathname.includes('auth/bypass')

  // Centralized IndexedDB cleanup: clear when user is unauthenticated
  useIndexedDbCleanup({
    isUnauthenticated:
      // no auth tokens in localStorage
      !hasAuthSession ||
      // showing tenant discovery (definitely not authenticated, but may have tokens in localStorage)
      !shouldBypassTenantDiscovery ||
      // Auth0 confirmed tokens are stale/invalid
      auth0ConfirmedUnauth
  })

  if (!shouldBypassTenantDiscovery) {
    return (
      <UnauthenticatedStatsigClientProvider>
        <TenantDiscoveryWrapper onTenantSet={() => setTenantJustSet(true)} />
      </UnauthenticatedStatsigClientProvider>
    )
  }

  return (
    <ConditionalAuth0Provider>
      <AuthWrapper onConfirmedUnauthenticated={() => setAuth0ConfirmedUnauth(true)}>
        <AuthenticatedStatsigClientProvider>
          {/* Theme needs to be nested under the AuthWrapper so the language preference is available */}
          <ThemeComponent>
            <LocalizationProvider>
              <AuthGuard fallback={<LoadingMessage />}>
                <AclGuard aclAbilities={defaultACLObj}>
                  {/* Above `DataManagementProvider` on purpose: the global vehicles/sites
                      pollers live there, and dashboard fullscreen mode needs them to keep
                      polling while the tab is unfocused (ENG-10448). */}
                  <FullscreenModeProvider>
                    <DataManagementProvider>
                      <UserLayout>{children}</UserLayout>
                    </DataManagementProvider>
                  </FullscreenModeProvider>
                </AclGuard>
              </AuthGuard>
              <Snackbar />
              {/* Inside the Statsig provider on purpose — it reads
                  `synop_internal_only` to decide whether it exists at all. */}
              <FeatureFlagsLauncher />
            </LocalizationProvider>
          </ThemeComponent>
        </AuthenticatedStatsigClientProvider>
      </AuthWrapper>
    </ConditionalAuth0Provider>
  )
}

function ConditionalAuth0Provider({ children }: PropsWithChildren) {
  const router = useRouter()
  const { origin } = useLocation()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const customerAssetsByUrl = getCustomerAssets()
  const customerAssetsByDiscovery = useCustomerAssetsByTenant()

  // Set up the auth0 provider
  // If there is a customerAsset associated with the URL, use that
  // Else if there is a customerAsset associated to the user's stored tenant, use that
  // Finally just use the defaults

  // Create redirect URI with current path and search params as parameters.
  const baseRedirectUri = customerAssetsByDiscovery?.appUrl ?? origin
  const search = searchParams?.toString() ?? ''
  const currentUrl = pathname + (search ? `?${search}` : '')
  const redirectUri = `${baseRedirectUri}?path=${encodeURIComponent(currentUrl)}`

  const handleRedirectCallback = () => {
    // Get the route parameter from the URL
    const urlParams = new URLSearchParams(window.location.search)
    const routeParam = urlParams.get('path')

    // Redirect to the original route or dashboard
    const redirectPath = routeParam && routeParam !== '/' ? routeParam : '/dashboard'
    router.replace(redirectPath)
  }

  return (
    <Auth0Provider
      domain={customerAssetsByUrl?.loginDomain ?? customerAssetsByDiscovery?.loginDomain ?? envConfig.auth0.domain}
      clientId={
        customerAssetsByUrl?.auth0ClientId ?? customerAssetsByDiscovery?.auth0ClientId ?? envConfig.auth0.clientId
      }
      useRefreshTokens={true}
      cacheLocation="localstorage"
      onRedirectCallback={handleRedirectCallback}
      authorizationParams={{
        redirect_uri: redirectUri,
        // The audience params in auth0 have the trailing slash. Add it here to match
        audience: `${envConfig.apiUrl}/`,
        scope: 'openid profile email offline_access'
      }}
    >
      {children}
    </Auth0Provider>
  )
}
